import { ButtonIcon, Dialog, useDialog } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import BigNumber from "bignumber.js";
import { useFieldArray, useForm } from "react-hook-form";
import { TbDeviceFloppy, TbPlus, TbTrash } from "react-icons/tb";
import {
  estimateOfferBalance,
  formatAddress,
  formatMoney,
  formatPercent,
  ManageOfferBodySchema,
  type MortgageFee,
  type Offer,
  sum,
  toBigNumber,
} from "shared";
import { useSuspenseAddress } from "@/addresses/hooks";
import { Api } from "@/api.config";
import { useSuspenseBorrower } from "@/borrower/hooks";
import { Button } from "@/components/Button";
import { FeeListFields } from "@/components/form/FeeListFields";
import { NumberInput } from "@/components/form/NumberInput";
import { TextField } from "@/components/form/TextField";
import { QueryKeys } from "@/LinkGen";
import { useMortgage } from "@/mortgages/hooks";
import styles from "./ManageOfferDialog.module.css";

function getOfferDefault(offers: Offer[] | undefined) {
  if (!offers || offers.length === 0) {
    return [
      {
        ID: null,
        name: "Long",
        investor: "",
        interest_rate: "",
        discount_points: "",
      },
      {
        ID: null,
        name: "Medium",
        investor: "",
        interest_rate: "",
        discount_points: "",
      },
      {
        ID: null,
        name: "Short",
        investor: "",
        interest_rate: "",
        discount_points: "",
      },
    ];
  }

  return offers.map((offer) => ({
    ID: offer.ID,
    name: offer.name,
    investor: offer.investor,
    interest_rate: new BigNumber(offer.data.interestRate),
    discount_points: new BigNumber(offer.data.discountPoints),
  }));
}

interface AddOfferDialogProps {
  mortgageID: number;
  onConfirm(id: number): unknown;
  offers?: Offer[];
  fees: MortgageFee[];
}

export const ManageOfferDialog = (props: AddOfferDialogProps) => {
  const { offers } = props;
  const mortgage = useMortgage(props.mortgageID);
  const borrower = useSuspenseBorrower(mortgage.clientID);
  const address = useSuspenseAddress(mortgage.addressID);
  const offer = offers?.[0];

  const dialog = useDialog();

  const { handleSubmit, formState, control, watch } = useForm({
    resolver: zodResolver(ManageOfferBodySchema),
    defaultValues: {
      offers: getOfferDefault(offers),
      mortgage_insurance: new BigNumber(
        offer?.data.mortgageInsurance.percentage ?? 0,
      ),
      years: offer?.data.years,
      amount_due: new BigNumber(offer?.data.amountDue ?? 0),
      compensation: offer?.data.compensation ?? "",
      fees: props.fees.map((fee) => ({ name: fee.name, amount: fee.amount })),
    },
  });
  const queryClient = useQueryClient();

  const onSubmit = handleSubmit(async (values) => {
    for (const offer of values.offers) {
      await Api.MortgageOffersManage(props.mortgageID, {
        ...values,
        ...offer,
      });
    }

    await queryClient.refetchQueries({
      queryKey: QueryKeys.Mortgage(props.mortgageID),
    });

    dialog.close();
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "offers",
  });

  const values = watch();

  const estimate = estimateOfferBalance({
    amountDue: (values.amount_due as BigNumber | null) || new BigNumber(0),
    closingCost: sum(
      (values.fees ?? []).map(
        (fee) =>
          toBigNumber(fee?.amount as BigNumber.Value | null) ??
          new BigNumber(0),
      ),
    ),
    compensation: new BigNumber(
      (values.compensation as string | BigNumber) || 0,
    ),
    currentMortgageBalance: mortgage.data.balance,
    fundingDate: mortgage.data.payoff.fundingDate,
    insurance: new BigNumber(
      mortgage.remortgage.prepays.propertyInsurance.amount ?? 0,
    ),
    taxes: new BigNumber(mortgage.remortgage.prepays.propertyTax.amount ?? 0),
    discountPointsPercentage: new BigNumber(0),
    interestRate: mortgage.data.interestRate,
    otherDebtsTotal:
      sum(mortgage.remortgage.debts.map((debt) => debt.balance)) ??
      new BigNumber(0),
  });

  const estimateLtv = address.value
    ? estimate.div(address.value).multipliedBy(100)
    : null;

  return (
    <form onSubmit={onSubmit}>
      <Dialog.Header>Manage offers</Dialog.Header>
      <Dialog.Body>
        <div className={styles.offerEstimate}>
          Property value: {formatMoney(address.value)}
          <br />
          Estimated loan value: {formatMoney(estimate)}
          <br />
          Estimated LTV: {formatPercent(estimateLtv, 1)}
          <br />
          Credit score: {borrower.creditScore ?? "-"}
          <br />
          Address:{" "}
          {formatAddress([
            address.line1,
            address.line2,
            address.city,
            address.state,
            address.zip_code,
          ])}
        </div>
        <h3 className="mb-3">Offers</h3>
        {fields.map((_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: No other way
          <div className={styles.offerItem} key={index}>
            <div className={styles.offerItemFields}>
              <TextField
                control={control}
                required
                name={`offers.${index}.name`}
                label="Name"
              />
              <TextField
                control={control}
                name={`offers.${index}.investor`}
                placeholder="Investor"
                label="Investor"
              />
              <NumberInput
                label="Interest rate"
                placeholder="5.00"
                required
                dp={4}
                control={control}
                name={`offers.${index}.interest_rate`}
              />
              <NumberInput
                label="Discount points (%)"
                placeholder="1.00"
                required
                dp={4}
                control={control}
                name={`offers.${index}.discount_points`}
              />
            </div>
            <ButtonIcon
              icon={TbTrash}
              className={styles.offerItemDelete}
              onClick={() => remove(index)}
            >
              Delete
            </ButtonIcon>
          </div>
        ))}
        <Button
          icon={TbPlus}
          onClick={() =>
            append({
              ID: null,
              name: "",
              investor: "",
              discount_points: "",
              interest_rate: "",
            })
          }
        >
          Add new offer
        </Button>
      </Dialog.Body>
      <hr />
      <Dialog.Body>
        <NumberInput
          label="Years"
          placeholder="Years"
          dp={0}
          required
          control={control}
          name="years"
        />
        <NumberInput
          label="Mortgage insurance (%)"
          placeholder="Mortgage insurance"
          dp={3}
          required
          control={control}
          name="mortgage_insurance"
        />
        <NumberInput
          label="Compensation ($)"
          placeholder="1999.00"
          required
          control={control}
          name="compensation"
        />
        <NumberInput
          label="Amount due at closing ($)"
          placeholder="0.00"
          required
          control={control}
          name="amount_due"
        />
      </Dialog.Body>
      <hr />
      <Dialog.Body>
        <h3 className="mb-3">Fees</h3>
        <FeeListFields control={control} name="fees" />
      </Dialog.Body>
      <Dialog.Footer>
        <Button
          icon={TbDeviceFloppy}
          type="submit"
          loading={formState.isSubmitting}
          // disabled={!formState.isValid}
        >
          Save
        </Button>
      </Dialog.Footer>
    </form>
  );
};
